import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, Users, CheckCircle, Heart, Euro, FileText, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const Transparenz = () => {
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/#donate');
    setTimeout(() => {
      const element = document.getElementById('donate');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };
  const transparencyItems = [
    {
      icon: Users,
      title: "Wer wir sind",
      content: "hay - humanity. action. you. e.V. ist ein gemeinnütziger Verein aus Bochum, initiiert von vier Geschwistern mit palästinensisch-deutschem Hintergrund: Stefan, Thomas, Daniel und Nadja. Gemeinsam mit ihren Eltern Abdul und Brigitte und ihrem langjährigen Freund Tim wurde der Verein 2025 gegründet. Unser Ziel: Humanitäre Hilfe für Menschen in Not – mit Schwerpunkt auf akuten Krisen wie im Gazastreifen."
    },
    {
      icon: CheckCircle,
      title: "Gemeinnützigkeit",
      content: "Wir befinden uns aktuell im Anerkennungsverfahren zur Gemeinnützigkeit. Spenden können nach erfolgter Anerkennung (nicht rückwirkend) steuerlich geltend gemacht werden. Sobald die Gemeinnützigkeit bestätigt ist, stellen wir gerne entsprechende Zuwendungsbestätigungen aus. Fragen dazu beantworten wir gerne unter: info@hayforhumanity.de"
    },
    {
      icon: Heart,
      title: "Finanzierung",
      content: "Unsere Hilfe basiert ausschließlich auf Spenden. Wir verzichten auf Verwaltungskosten – alles, was gespendet wird, geht direkt in die Hilfe vor Ort."
    },
    {
      icon: Euro,
      title: "Spendenverwendung",
      content: "Jede Spende wird dokumentiert. Wir arbeiten mit Vertrauenspersonen in Gaza und Ägypten zusammen."
    }
  ];

  return (
    <div className="min-h-screen bg-warm-beige pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center space-x-2 text-olive-green hover:text-olive-dark transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Zurück zur Startseite</span>
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-bold text-charcoal font-heading text-center">
                Unsere Transparenz.
              </h1>
            </div>
            <p className="text-lg sm:text-xl text-charcoal/80 max-w-2xl mx-auto leading-relaxed">
              Für euer Vertrauen.
            </p>
            <div className="w-24 h-1 bg-olive-green mx-auto mt-6"></div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 border border-olive-green/10 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Transparency Items */}
            <div className="space-y-8 sm:space-y-12">
              {transparencyItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex items-start space-x-4 sm:space-x-6"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-olive-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-olive-green" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl sm:text-2xl font-bold text-charcoal mb-3 sm:mb-4 font-heading">
                      {index + 1}. {item.title}
                    </h2>
                    <p className="text-charcoal/80 leading-relaxed text-base sm:text-lg">
                      {item.content}
                    </p>
                  </div>
                </motion.div>
              ))}

            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            className="bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-6 sm:p-8 md:p-12 text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="text-center">
              <div className="mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold font-heading text-center">
                  Kontakt & Austausch
                </h2>
              </div>
              <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto">
                Wir beantworten gern Fragen zur Spendenverwendung. Transparenz ist für uns Teil von Verantwortung.
              </p>
              <div className="mt-8">
                <a 
                  href="mailto:info@hayforhumanity.de"
                  className="inline-flex items-center space-x-2 bg-white text-olive-green px-6 py-3 sm:px-8 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all transform hover:scale-105 shadow-xl"
                >
                  <Mail className="w-5 h-5" />
                  <span>info@hayforhumanity.de</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            className="mt-8 p-6 bg-white/80 backdrop-blur-sm rounded-xl border border-olive-green/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
          >
            <div className="flex items-start space-x-3">
              <FileText className="w-6 h-6 text-olive-green flex-shrink-0 mt-1" />
              <p className="text-charcoal/70 leading-relaxed">
                Diese Transparenz-Erklärung wird regelmäßig aktualisiert. Änderungen unserer Vereinsstruktur oder 
                unseres Status werden hier zeitnah kommuniziert.
              </p>
            </div>
          </motion.div>

          {/* Donate CTA */}
          <motion.div
            className="mt-8 bg-gradient-to-r from-olive-green to-olive-dark rounded-2xl p-8 text-white text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4 font-heading">
              Unterstütze unsere Mission.
            </h3>
            <p className="text-lg text-white/90 mb-6 leading-relaxed max-w-2xl mx-auto">
              Jede Spende hilft uns, Menschen in Not zu unterstützen.
            </p>
            <button
              onClick={handleDonateClick}
              className="bg-white text-olive-green hover:bg-cream px-8 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Jetzt spenden
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Transparenz;